/**
 * ErgoBot table runtime: the runtime contract, version 1.3, as running code.
 *
 * It chooses the finger numbers a phrase is shown with, says how much each
 * number is needed, and says whether a phrase can follow the previous one from
 * where it left the hand. Everything is worked out on what the written notes
 * sound as in the key in force.
 *
 * Everything here reads the compiled table bundle and nothing else. Nothing is a
 * rule about music: every cost, threshold, finger map and scale fingering is a
 * number in the tables. All arithmetic is on integers.
 *
 * This folder imports nothing from outside itself, so it can be copied whole
 * into another code base.
 */
import type {
  Arrival,
  DisplayLevel,
  Figure,
  Finger,
  Hand,
  HandInput,
  HandResult,
  HandState,
  HandTable,
  Join,
  JoinMode,
  Key,
  Line,
  Motion,
  MotionHandTable,
  NeedConstants,
  PositionHandTable,
  Side,
  Tables,
} from './types';

/** The contract version this runtime implements. */
export const CONTRACT_VERSION = '1.3';

const TABLES_FORMAT = 'ergobot-tables';
const FINGERS: ReadonlyArray<Finger> = [1, 2, 3, 4, 5];
const HANDS: ReadonlyArray<Hand> = ['rh', 'lh'];
const MOTIONS: ReadonlyArray<Motion> = [
  'none',
  'stretch',
  'squeeze',
  'pivot',
  'substitution',
  'hop',
  'leap',
];
const BLACK_PITCH_CLASSES: ReadonlyArray<number> = [1, 3, 6, 8, 10];
const LETTERS = 'CDEFGAB';
const NO_ALTERATION: ReadonlyArray<number> = [0, 0, 0, 0, 0, 0, 0];
const OCTAVE_PATTERN = /^-?[0-9]+$/;
const UNREACHABLE = Number.POSITIVE_INFINITY;
const NOT_ALLOWED = -1;
const TOP_SCORE = 100;
const WHITE = 0;
const BLACK = 1;
const RESTING_SHAPE = 0;
const ANY_FINGER = 0;
const NEVER_COMPATIBLE = 'x';

/** A finger and the shape the hand is in, as one number: 5 * shape + (finger - 1). */
type Seat = number;
/** The cost of every seat a note can be reached in. */
type Layer = ReadonlyMap<Seat, number>;
/** A way onto a note: the seat it lands in and what it costs. */
type Move = readonly [Seat, number];
/** Every way on from each seat of one note to the next note. */
type Edges = ReadonlyMap<Seat, ReadonlyArray<Move>>;

/** Where a linked move stands: joining two phrases, inside the line, or inside
 *  the line across a rest, the hand held as it is. */
type Where = 'joining' | 'within' | 'across-rest';

/** The moves between two keys before the shape of the hand comes in: per
 *  finger pair, at 5 * (finger1 - 1) + (finger2 - 1), what the move costs
 *  (NOT_ALLOWED when it is not one) and, for linked moves, its change of shape.
 *  `ops` is null when the hand is placed again: it then lands in the resting shape. */
interface Step {
  costs: ReadonlyArray<number>;
  ops: ReadonlyArray<number> | null;
}

/** A key with its alterations spelled out. */
interface FullKey {
  transposition: number;
  alter: ReadonlyArray<number>;
}

/** The keys of a hand table as they sound in a key. */
interface Keyboard {
  midi: ReadonlyArray<number>;
  /** 0 white, 1 black. */
  colours: ReadonlyArray<number>;
  /** Which of a motion hand's cost planes applies. */
  keyClass: number;
  /** The finger each pitch is given in a scalar run in this key, if the hand
   *  has a scale for it. */
  scale: ReadonlyArray<number> | null;
}

/** A note before the standard patterns are looked for. */
interface PlainNote {
  /** Of the pitch in the hand table. */
  index: number;
  name: string;
  staffStep: number;
  colour: number;
  afterRest: boolean;
}

interface Note extends PlainNote {
  /** The finger a standard pattern gives the note; ANY_FINGER when none does. */
  pattern: number;
}

/** How the first note is entered: with nothing known, or from a hand state on
 *  the pitch `held` of the table. */
type Entry =
  | { mode: 'placed' }
  | { mode: 'rest' | 'linked' | 'replaced'; held: number; state: HandState };

interface Search {
  entry: Entry;
  forward: ReadonlyArray<Layer>;
  backward: ReadonlyArray<Layer>;
  edges: ReadonlyArray<Edges>;
}

/** A fingering being followed through the search: its fingers so far. */
type Trail = ReadonlyArray<Finger>;

const AS_WRITTEN: FullKey = { transposition: 0, alter: NO_ALTERATION };
const FREE_JOIN: Join = { compatible: true, hops: 0 };
const NO_JOIN: Join = { compatible: false, hops: 0 };

const notOk = (): HandResult => ({
  ok: false,
  fingers: [],
  need: [],
  arrivals: [],
  gaps: [],
  cost: 0,
  join: 'placed',
  state: null,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const ownValue = <Value>(
  record: Readonly<Record<string, Value>>,
  key: string,
): Value | undefined =>
  Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;

/** The running results of folding `items` from `initial`, `initial` included. */
const accumulate = <Item, Result>(
  items: ReadonlyArray<Item>,
  fold: (result: Result, item: Item) => Result,
  initial: Result,
): Result[] =>
  items.reduce<Result[]>(
    (results, item) => results.concat([fold(results[results.length - 1], item)]),
    [initial],
  );

const seatOf = (finger: Finger, shape: number): Seat => 5 * shape + (finger - 1);
const fingerOf = (seat: Seat): Finger => ((seat % 5) + 1) as Finger;
const shapeOf = (seat: Seat): number => Math.floor(seat / 5);

// ---------------------------------------------------------------------------
// The bundle (contract, section 2)
// ---------------------------------------------------------------------------

/** The table bundle, refused unless it is one this runtime implements. */
export function checkedTables(bundle: unknown): Tables {
  if (!isRecord(bundle) || bundle.format !== TABLES_FORMAT) {
    throw new Error('not an ErgoBot table bundle');
  }
  if (bundle.contract_version !== CONTRACT_VERSION) {
    throw new Error(
      `contract version ${String(bundle.contract_version)} is not ${CONTRACT_VERSION}`,
    );
  }
  return bundle as unknown as Tables;
}

/** The table of one hand of a tier, or null when the tier does not use that hand. */
export function handTable(tables: Tables, tier: string, hand: Hand): HandTable | null {
  if (HANDS.indexOf(hand) < 0) {
    throw new RangeError(`hand must be one of ${HANDS.join(', ')}, not ${String(hand)}`);
  }
  const tableId = ownValue(tables.tiers, tier)?.[hand];
  return tableId === undefined || tableId === null
    ? null
    : (ownValue(tables.hands, tableId) ?? null);
}

// ---------------------------------------------------------------------------
// Pitches, keys and positions (contract, sections 1.1, 1.3 and 1.4)
// ---------------------------------------------------------------------------

const letterIndex = (name: string): number => LETTERS.indexOf(String(name).charAt(0).toUpperCase());

/** 7 * octave + the index of the letter in CDEFGAB (C4 = 28); accidentals are ignored. */
export function staffStep(name: string): number {
  const octave = String(name)
    .slice(1)
    .split('')
    .filter((character) => (character >= '0' && character <= '9') || character === '-')
    .join('');
  if (String(name) === '' || letterIndex(name) < 0 || !OCTAVE_PATTERN.test(octave)) {
    throw new RangeError(`not a pitch name: ${String(name)}`);
  }
  return 7 * parseInt(octave, 10) + letterIndex(name);
}

const thumbStep = (side: Side, step: number, finger: Finger): number =>
  side === 'right' ? step - (finger - 1) : step + (finger - 1);

/** The staff step under the thumb when the hand rests with `finger` on the key. */
export function position(side: Side, name: string, finger: Finger): number {
  return thumbStep(side, staffStep(name), finger);
}

/** 1 for a black key, 0 for a white one. */
export function colourOf(midi: number): number {
  return BLACK_PITCH_CLASSES.indexOf(((midi % 12) + 12) % 12) >= 0 ? BLACK : WHITE;
}

function checkedLine(line: Line): Line {
  if (!line.every((token) => token === null || typeof token === 'string')) {
    throw new TypeError('a line holds pitch names and null for rests');
  }
  return line;
}

function checkedKey(key: Key | null | undefined): FullKey {
  if (key === undefined || key === null) return AS_WRITTEN;
  if (!isRecord(key)) throw new TypeError('a key is a Key');
  const alter = key.alter ?? NO_ALTERATION;
  const whole =
    Number.isInteger(key.transposition) &&
    Array.isArray(alter) &&
    alter.length === LETTERS.length &&
    alter.every((semitones) => Number.isInteger(semitones));
  if (!whole) {
    throw new RangeError('a key has a whole transposition and one whole alteration per letter');
  }
  return { transposition: key.transposition, alter };
}

function checkedState(state: HandState | null | undefined): HandState | null {
  if (state === undefined || state === null) return null;
  if (!isRecord(state)) throw new TypeError('a hand state is a HandState or null');
  if (FINGERS.indexOf(state.finger) < 0) {
    throw new RangeError(`not a finger: ${String(state.finger)}`);
  }
  const shape = state.shape ?? RESTING_SHAPE;
  if (!Number.isInteger(state.midi) || !Number.isInteger(shape) || shape < 0) {
    throw new RangeError('a hand state has a MIDI number and a shape number');
  }
  return state;
}

/** The colours of a hand's keys as one number: bit `p` is set when pitch `p` is black. */
const colourPattern = (colours: ReadonlyArray<number>): number =>
  colours.reduce((pattern, colour, index) => pattern + colour * 2 ** index, 0);

/** The hand's keys as they sound in a key (contract 5.1, step 1). */
function keyboardOf(table: HandTable, key: FullKey): Keyboard {
  const midi = table.midi.map(
    (written, index) => written + key.transposition + key.alter[letterIndex(table.pitches[index])],
  );
  const colours = midi.map(colourOf);
  const colouring = colourPattern(colours);
  const scale =
    table.kind === 'motion' ? ownValue(table.scales ?? {}, String(colouring)) : undefined;
  return {
    midi,
    colours,
    keyClass: table.kind === 'motion' ? table.key_class[colouring] : 0,
    scale: scale ?? null,
  };
}

/** Per note, how many notes the stretch around it holds in which every note is
 *  one staff step in `direction` from the note before. `steps[i]` is the staff
 *  steps from note `i` to note `i + 1` (0 across a rest). */
const runLengths = (steps: ReadonlyArray<number>, direction: number): number[] => {
  const counted = (moves: ReadonlyArray<number>): number[] =>
    accumulate(moves, (run: number, move) => (move === direction ? run + 1 : 0), 0);
  const behind = counted(steps);
  const ahead = counted(steps.slice().reverse()).reverse();
  return behind.map((run, i) => 1 + run + ahead[i]);
};

/** Per note, whether it is part of a scalar run (contract 5.1, step 5):
 *  `pattern.run_notes` or more notes in a row, each a staff step from the one
 *  before, all up or all down, no rest between them. */
const inScalarRun = (tables: Tables, notes: ReadonlyArray<PlainNote>): boolean[] => {
  const steps = notes
    .slice(1)
    .map((note, i) => (note.afterRest ? 0 : note.staffStep - notes[i].staffStep));
  const up = runLengths(steps, 1);
  const down = runLengths(steps, -1);
  return notes.map((_note, i) => Math.max(up[i], down[i]) >= tables.pattern.run_notes);
};

/** Whether a line is the figure (contract 2.6): it plays every key of the
 *  figure and no other and, where the figure is in order, no note is further
 *  than one place in the figure's keys from the note before. */
const isFigure = (figure: Figure, notes: ReadonlyArray<PlainNote>): boolean => {
  const places = notes.map((note) => figure.keys.indexOf(note.index));
  const playsItsKeysOnly =
    places.every((place) => place >= 0) &&
    figure.keys.every((_key, place) => places.indexOf(place) >= 0);
  return (
    playsItsKeysOnly &&
    (!figure.in_order || places.slice(1).every((place, i) => Math.abs(place - places[i]) <= 1))
  );
};

/** Per note, the finger a standard pattern gives it (contract 5.1, step 5),
 *  ANY_FINGER where none does: in a scalar run the finger of the scale the hand
 *  has in this key, else the finger of the first figure the whole line is.
 *  Where `pattern.whole_line` is set, a run takes the scale only in a line that
 *  is nothing but scalar runs. */
const patternsOf = (
  tables: Tables,
  table: HandTable,
  keyboard: Keyboard,
  notes: ReadonlyArray<PlainNote>,
): number[] => {
  const figures = table.kind === 'motion' ? (table.figures ?? []) : [];
  const figure = figures.find((candidate) => isFigure(candidate, notes));
  const scale = keyboard.scale;
  const inRun = scale === null ? notes.map(() => false) : inScalarRun(tables, notes);
  const takesScale = !tables.pattern.whole_line || inRun.every((isInRun) => isInRun);
  return notes.map((note, i) => {
    if (scale !== null && inRun[i] && takesScale) return scale[note.index];
    return figure === undefined ? ANY_FINGER : figure.fingers[note.index];
  });
};

/** The notes of a line (contract 5.1), or null when a pitch is not the hand's. */
function notesOf(tables: Tables, table: HandTable, keyboard: Keyboard, line: Line): Note[] | null {
  if (line.some((token) => token !== null && table.pitches.indexOf(token) < 0)) return null;
  const places = line
    .map((token, place) => (token === null ? -1 : place))
    .filter((place) => place >= 0);
  const plain = places.map((place, order): PlainNote => {
    const name = line[place] as string;
    const index = table.pitches.indexOf(name);
    const previousPlace = order === 0 ? -1 : places[order - 1];
    return {
      index,
      name,
      staffStep: staffStep(name),
      colour: keyboard.colours[index],
      afterRest: line.slice(previousPlace + 1, place).some((token) => token === null),
    };
  });
  const patterns = patternsOf(tables, table, keyboard, plain);
  return plain.map((note, i) => ({ ...note, pattern: patterns[i] }));
}

/** The pitch of the table the state's key is, or null: no state, or a key the
 *  hand does not have in this key (contract 5.1, step 3). */
const stateIndex = (keyboard: Keyboard, state: HandState | null): number | null => {
  const index = state === null ? -1 : keyboard.midi.indexOf(state.midi);
  return index < 0 ? null : index;
};

const leftState = (
  keyboard: Keyboard,
  notes: ReadonlyArray<Note>,
  fingers: ReadonlyArray<Finger>,
  shape: number,
  line: Line,
): HandState => {
  const rested = line[line.length - 1] === null;
  return {
    midi: keyboard.midi[notes[notes.length - 1].index],
    finger: fingers[fingers.length - 1],
    rested,
    shape: rested ? RESTING_SHAPE : shape,
  };
};

/** A phrase in which the hand has no note: it keeps its key and finger, rested. */
const silentResult = (state: HandState | null): HandResult => ({
  ok: true,
  fingers: [],
  need: [],
  arrivals: [],
  gaps: [],
  cost: 0,
  join: state === null ? 'placed' : 'rest',
  state:
    state === null
      ? null
      : { midi: state.midi, finger: state.finger, rested: true, shape: RESTING_SHAPE },
});

// ---------------------------------------------------------------------------
// The need score (contract 5.4)
// ---------------------------------------------------------------------------

const motionCode = (arrival: Arrival | undefined): number =>
  arrival === undefined ? -1 : MOTIONS.indexOf(arrival as Motion);

/** 0 to 100 per note: how much its finger number is needed. */
export function needScores(
  constants: NeedConstants,
  notes: ReadonlyArray<{ name: string; staffStep: number }>,
  fingers: ReadonlyArray<Finger>,
  arrivals: ReadonlyArray<Arrival>,
  gaps: ReadonlyArray<ReadonlyArray<number | null>>,
): number[] {
  const count = notes.length;

  const firstBase = (arrival: Arrival): number => {
    if (arrival === 'placed') return constants.first.placed;
    if (arrival === 'replaced') return constants.first.replaced;
    if (arrival === 'rest-move') return constants.first.moved;
    if (arrival === 'rest' || arrival === 'none') return constants.first.same;
    return constants.first.motion;
  };

  const laterBase = (arrival: Arrival): number => {
    if (arrival === 'rest-move') return constants.rest_move;
    return motionCode(arrival) >= 0 ? constants.arrival[motionCode(arrival)] : 0;
  };

  const setup = (i: number): number => {
    const followingCode = motionCode(arrivals[i + 1]);
    return followingCode >= 1 ? constants.setup[followingCode] : 0;
  };

  const switchback = (i: number): number => {
    const turned =
      i >= 2 &&
      arrivals[i - 1] === 'pivot' &&
      (notes[i].staffStep - notes[i - 1].staffStep) *
        (notes[i - 1].staffStep - notes[i - 2].staffStep) <
        0;
    return turned ? constants.switchback : 0;
  };

  const last = (i: number): number => (i === count - 1 && count > 1 ? constants.last : 0);

  const repeated = (i: number): boolean =>
    i >= 1 &&
    arrivals[i] === 'none' &&
    notes[i].name === notes[i - 1].name &&
    fingers[i] === fingers[i - 1];

  const ambiguous = (i: number): boolean =>
    FINGERS.some((finger, column) => {
      const gap = gaps[i][column];
      return finger !== fingers[i] && gap !== null && gap < constants.ambiguous_gap;
    });

  const score = (i: number): number => {
    const base = i === 0 ? firstBase(arrivals[i]) : laterBase(arrivals[i]);
    const highest = Math.max(base, setup(i), switchback(i), last(i));
    if (highest >= constants.keep) return highest;
    if (repeated(i)) return 0;
    return ambiguous(i) ? Math.floor(highest / 2) : highest;
  };

  return notes.map((_note, i) => score(i));
}

/** The lowest need score a display level shows: 'none', 'minimal', 'all', or a
 *  slider percentage (0 to 100). */
export function needThreshold(tables: Tables, level: DisplayLevel | number): number {
  if (typeof level === 'number') return TOP_SCORE - level;
  if (level === 'none') return UNREACHABLE;
  if (level !== 'minimal' && level !== 'all') {
    throw new RangeError(`level must be none, minimal, all or a percentage, not ${String(level)}`);
  }
  return tables.need.levels[level];
}

/** Which numbers a display level shows. */
export function shown(
  tables: Tables,
  need: ReadonlyArray<number>,
  level: DisplayLevel | number,
): boolean[] {
  const threshold = needThreshold(tables, level);
  return need.map((score) => score >= threshold);
}

// ---------------------------------------------------------------------------
// Position hands (contract 5.2)
// ---------------------------------------------------------------------------

function positionResult(
  tables: Tables,
  table: PositionHandTable,
  keyboard: Keyboard,
  notes: ReadonlyArray<Note>,
  line: Line,
  state: HandState | null,
): HandResult {
  const fingers = notes.map((note) => table.fingers[note.index]);
  const held = stateIndex(keyboard, state);
  const continues = state !== null && held !== null && table.fingers[held] === state.finger;
  const rested = state !== null && (state.rested || notes[0].afterRest);
  const join: JoinMode = !continues ? 'placed' : rested ? 'rest' : 'linked';
  const firstArrival: Arrival = join === 'linked' ? 'none' : join;
  const arrivals = notes.map(
    (note, i): Arrival => (i === 0 ? firstArrival : note.afterRest ? 'rest' : 'none'),
  );
  const gaps = fingers.map((mapped) => FINGERS.map((finger) => (finger === mapped ? 0 : null)));
  return {
    ok: true,
    fingers,
    need: needScores(tables.need, notes, fingers, arrivals, gaps),
    arrivals,
    gaps,
    cost: 0,
    join,
    state: leftState(keyboard, notes, fingers, RESTING_SHAPE, line),
  };
}

// ---------------------------------------------------------------------------
// Motion hands (contract 5.3)
// ---------------------------------------------------------------------------

const moveCell = (finger1: Finger, finger2: Finger): number => 5 * (finger1 - 1) + (finger2 - 1);

const mayRest = (
  table: MotionHandTable,
  keyboard: Keyboard,
  index: number,
  finger: Finger,
): boolean => table.place[keyboard.keyClass][index][finger - 1] !== NOT_ALLOWED;

/** What the note itself costs under a finger whatever the move: the colour of
 *  its key, and another finger than the one a standard pattern gives it. */
const noteCost = (tables: Tables, note: Note, finger: Finger): number =>
  tables.colour.unary[finger - 1][note.colour] +
  (note.pattern !== ANY_FINGER && note.pattern !== finger ? tables.pattern.cost : 0);

/** The hand is placed again on `note` from each finger on one key: any finger
 *  that may rest there, priced by how far the hand moves (`rest_move` during a
 *  rest, `replace_move` when there was none), and during a rest by `rest_hop`
 *  where the same finger takes the new key. The note's own cost is included. It
 *  lands in the resting shape whatever shape it was in. */
const freeStep = (
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  fromIndex: number,
  note: Note,
  mode: 'rest' | 'replaced',
): Step => {
  const costsByDistance = mode === 'replaced' ? table.replace_move : table.rest_move;
  const farthest = costsByDistance.length - 1;
  const fromStep = staffStep(table.pitches[fromIndex]);
  const costs = FINGERS.flatMap((fromFinger) => {
    const from = thumbStep(table.side, fromStep, fromFinger);
    return FINGERS.map((finger) => {
      if (!mayRest(table, keyboard, note.index, finger)) return NOT_ALLOWED;
      const apart = Math.abs(thumbStep(table.side, note.staffStep, finger) - from);
      const hops = mode === 'rest' && finger === fromFinger && apart > 0;
      return (
        costsByDistance[Math.min(apart, farthest)] +
        (hops ? tables.rest_hop[finger - 1] : 0) +
        noteCost(tables, note, finger)
      );
    });
  });
  return { costs, ops: null };
};

/** The linked moves from each finger on one key onto `note`, the note's own
 *  cost included and the cost of the change of shape left out: that one depends
 *  on the shape the hand is in. Across a rest the hand may be held as it is for
 *  the motions `across_rest` allows, onto a finger it could also have been
 *  placed with. */
const linkedStep = (
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  fromIndex: number,
  note: Note,
  where: Where,
): Step => {
  const bases = table.linked[keyboard.keyClass][fromIndex][note.index];
  const motions = table.motion[fromIndex][note.index];
  const colours = 2 * keyboard.colours[fromIndex] + note.colour;
  const acrossRest = where === 'across-rest';
  const costs = FINGERS.flatMap((fromFinger) =>
    FINGERS.map((finger) => {
      const cell = moveCell(fromFinger, finger);
      const motion = motions[cell];
      const held = acrossRest ? tables.across_rest[motion] : 0;
      const allowed =
        bases[cell] !== NOT_ALLOWED &&
        held !== NOT_ALLOWED &&
        (!acrossRest || mayRest(table, keyboard, note.index, finger));
      return allowed
        ? bases[cell] +
            held +
            tables.colour.pair[motion][fromFinger - 1][finger - 1][colours] +
            (where === 'within' ? tables.within[motion][finger - 1] : 0) +
            noteCost(tables, note, finger)
        : NOT_ALLOWED;
    }),
  );
  return { costs, ops: table.shape_op[fromIndex][note.index] };
};

/** The steps onto `note` from one key. `where` is 'joining' for the move from
 *  the hand state into the first note, 'within' for a move inside the line.
 *  Across a rest inside the line the hand is placed again or held as it is. */
const stepsOnto = (
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  mode: 'linked' | 'rest' | 'replaced',
  fromIndex: number,
  note: Note,
  where: 'joining' | 'within',
): Step[] => {
  if (mode === 'linked') return [linkedStep(tables, table, keyboard, fromIndex, note, where)];
  const free = freeStep(tables, table, keyboard, fromIndex, note, mode);
  return where === 'within'
    ? [free, linkedStep(tables, table, keyboard, fromIndex, note, 'across-rest')]
    : [free];
};

/** Every allowed way on from a seat over the steps onto a note. With `relaxed`
 *  this is the inner loop of the search, so it is written out. */
const movesFrom = (tables: Tables, steps: ReadonlyArray<Step>, seat: Seat): Move[] => {
  const fromFinger = fingerOf(seat);
  const fromShape = shapeOf(seat);
  const moves: Move[] = [];
  steps.forEach((step) => {
    FINGERS.forEach((finger) => {
      const cell = moveCell(fromFinger, finger);
      const cost = step.costs[cell];
      if (cost === NOT_ALLOWED) return;
      if (step.ops === null) {
        moves.push([seatOf(finger, RESTING_SHAPE), cost]);
        return;
      }
      const change = step.ops[cell];
      moves.push([
        seatOf(finger, tables.shape.next[change][fromShape]),
        cost + tables.shape.cost[change][fromShape],
      ]);
    });
  });
  return moves;
};

const firstEntry = (keyboard: Keyboard, state: HandState | null, first: Note): Entry => {
  const held = stateIndex(keyboard, state);
  if (state === null || held === null) return { mode: 'placed' };
  return { mode: state.rested || first.afterRest ? 'rest' : 'linked', held, state };
};

/** The cost of every seat the first note can be entered in, its own cost included. */
const entryLayer = (
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  entry: Entry,
  first: Note,
): Layer => {
  if (entry.mode === 'placed') {
    return new Map(
      FINGERS.filter((finger) => mayRest(table, keyboard, first.index, finger)).map(
        (finger): [Seat, number] => [
          seatOf(finger, RESTING_SHAPE),
          table.place[keyboard.keyClass][first.index][finger - 1] + noteCost(tables, first, finger),
        ],
      ),
    );
  }
  const shape = entry.state.shape ?? RESTING_SHAPE;
  const known = entry.mode === 'linked' && shape < tables.shape.count;
  const seat = seatOf(entry.state.finger, known ? shape : RESTING_SHAPE);
  const steps = stepsOnto(tables, table, keyboard, entry.mode, entry.held, first, 'joining');
  return new Map(movesFrom(tables, steps, seat));
};

/** Per seat of the next note, the lowest cost it is reached with. */
const relaxed = (layer: Layer, edges: Edges): Layer => {
  const next = new Map<Seat, number>();
  layer.forEach((cost, source) => {
    (edges.get(source) as ReadonlyArray<Move>).forEach((move) => {
      const through = cost + move[1];
      const reached = next.get(move[0]);
      if (reached === undefined || through < reached) next.set(move[0], through);
    });
  });
  return next;
};

/** Per seat of the earlier note that can still finish the line, the cheapest way on. */
const relaxedBack = (layer: Layer, edges: Edges): Layer => {
  const earlier = new Map<Seat, number>();
  edges.forEach((moves, source) => {
    const cheapest = moves.reduce((lowest, move) => {
      const onward = layer.get(move[0]);
      return onward === undefined ? lowest : Math.min(lowest, move[1] + onward);
    }, UNREACHABLE);
    if (cheapest < UNREACHABLE) earlier.set(source, cheapest);
  });
  return earlier;
};

function search(
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  entry: Entry,
  notes: ReadonlyArray<Note>,
): Search {
  const first = entryLayer(tables, table, keyboard, entry, notes[0]);
  const { forward, edges } = notes.slice(1).reduce<{ forward: Layer[]; edges: Edges[] }>(
    (found, note, i) => {
      const from = found.forward[found.forward.length - 1];
      const steps = stepsOnto(
        tables,
        table,
        keyboard,
        note.afterRest ? 'rest' : 'linked',
        notes[i].index,
        note,
        'within',
      );
      const onward = new Map<Seat, Move[]>();
      from.forEach((_cost, seat) => onward.set(seat, movesFrom(tables, steps, seat)));
      return {
        forward: found.forward.concat([relaxed(from, onward)]),
        edges: found.edges.concat([onward]),
      };
    },
    { forward: [first], edges: [] },
  );
  const finished = new Map<Seat, number>();
  forward[forward.length - 1].forEach((_cost, seat) => finished.set(seat, 0));
  const backward = accumulate(edges.slice().reverse(), relaxedBack, finished as Layer).reverse();
  return { entry, forward, backward, edges };
}

const isFound = (found: Search): boolean => found.forward[found.forward.length - 1].size > 0;

const bestOf = (found: Search): number => {
  let best = UNREACHABLE;
  found.forward[found.forward.length - 1].forEach((cost) => {
    if (cost < best) best = cost;
  });
  return best;
};

/** Whether `trail` comes before `other`: the lower finger at the first note
 *  where they differ. */
const comesBefore = (trail: Trail, other: Trail): boolean => {
  const differs = trail.findIndex((finger, i) => finger !== other[i]);
  return differs >= 0 && trail[differs] < other[differs];
};

/**
 * The fingers, and the shape the hand is left in, of the optimal fingering
 * whose costs fall latest (contract 5.3, step 5): note by note the lower step
 * cost wins at the first difference, and among fingerings with the same step
 * costs the lowest finger at the first note where they differ.
 */
function pathOf(found: Search): { fingers: Finger[]; shape: number } {
  const best = bestOf(found);
  const entered = Array.from(found.forward[0].entries()).filter(([seat, cost]) => {
    const onward = found.backward[0].get(seat);
    return onward !== undefined && cost + onward === best;
  });
  const cheapestEntry = Math.min(...entered.map(([, cost]) => cost));
  const start = new Map<Seat, Trail>(
    entered
      .filter(([, cost]) => cost === cheapestEntry)
      .map(([seat]): [Seat, Trail] => [seat, [fingerOf(seat)]]),
  );

  /** The trails that go on over the cheapest optimal moves into note `i`. */
  const extend = (trails: ReadonlyMap<Seat, Trail>, edges: Edges, i: number) => {
    const optimal: Array<{ seat: Seat; cost: number; source: Seat }> = [];
    trails.forEach((_trail, source) => {
      const before = found.forward[i - 1].get(source) as number;
      (edges.get(source) as ReadonlyArray<Move>).forEach(([seat, cost]) => {
        const after = found.backward[i].get(seat);
        if (after !== undefined && before + cost + after === best) {
          optimal.push({ seat, cost, source });
        }
      });
    });
    const least = optimal.reduce((lowest, move) => Math.min(lowest, move.cost), UNREACHABLE);
    const kept = new Map<Seat, Trail>();
    optimal
      .filter((move) => move.cost === least)
      .forEach((move) => {
        const trail = (trails.get(move.source) as Trail).concat([fingerOf(move.seat)]);
        const known = kept.get(move.seat);
        if (known === undefined || comesBefore(trail, known)) kept.set(move.seat, trail);
      });
    return kept;
  };

  const trails = found.edges.reduce<ReadonlyMap<Seat, Trail>>(
    (kept, edges, i) => extend(kept, edges, i + 1),
    start,
  );
  // Ties between seats go to the lower shape: seats are visited in rising order.
  const [lastSeat, fingers] = Array.from(trails.entries())
    .sort(([seat], [other]) => seat - other)
    .reduce((chosen, candidate) => (comesBefore(candidate[1], chosen[1]) ? candidate : chosen));
  return { fingers: fingers.slice(), shape: shapeOf(lastSeat) };
}

const gapsOf = (found: Search): Array<Array<number | null>> => {
  const best = bestOf(found);
  return found.forward.map((layer, i) => {
    const through: number[] = FINGERS.map(() => UNREACHABLE);
    layer.forEach((cost, seat) => {
      const onward = found.backward[i].get(seat);
      const column = seat % 5;
      if (onward !== undefined && cost + onward < through[column]) through[column] = cost + onward;
    });
    return through.map((cost) => (cost === UNREACHABLE ? null : cost - best));
  });
};

const freeArrival = (
  table: MotionHandTable,
  fromIndex: number,
  fromFinger: Finger,
  note: Note,
  finger: Finger,
): Arrival =>
  thumbStep(table.side, note.staffStep, finger) ===
  position(table.side, table.pitches[fromIndex], fromFinger)
    ? 'rest'
    : 'rest-move';

const motionName = (
  table: MotionHandTable,
  fromIndex: number,
  fromFinger: Finger,
  note: Note,
  finger: Finger,
): Arrival => MOTIONS[table.motion[fromIndex][note.index][moveCell(fromFinger, finger)]];

function arrivalsOf(
  table: MotionHandTable,
  entry: Entry,
  notes: ReadonlyArray<Note>,
  fingers: ReadonlyArray<Finger>,
): Arrival[] {
  const first = (): Arrival => {
    if (entry.mode === 'placed' || entry.mode === 'replaced') return entry.mode;
    return entry.mode === 'rest'
      ? freeArrival(table, entry.held, entry.state.finger, notes[0], fingers[0])
      : motionName(table, entry.held, entry.state.finger, notes[0], fingers[0]);
  };
  const later = (i: number): Arrival =>
    notes[i].afterRest
      ? freeArrival(table, notes[i - 1].index, fingers[i - 1], notes[i], fingers[i])
      : motionName(table, notes[i - 1].index, fingers[i - 1], notes[i], fingers[i]);
  return notes.map((_note, i) => (i === 0 ? first() : later(i)));
}

/** The search in the first note's mode, falling back from linked to replaced. */
function bestSearch(
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  state: HandState | null,
  notes: ReadonlyArray<Note>,
): Search {
  const entry = firstEntry(keyboard, state, notes[0]);
  const found = search(tables, table, keyboard, entry, notes);
  if (isFound(found) || entry.mode !== 'linked') return found;
  return search(tables, table, keyboard, { ...entry, mode: 'replaced' }, notes);
}

function motionResult(
  tables: Tables,
  table: MotionHandTable,
  keyboard: Keyboard,
  notes: ReadonlyArray<Note>,
  line: Line,
  state: HandState | null,
): HandResult {
  const found = bestSearch(tables, table, keyboard, state, notes);
  if (!isFound(found)) return notOk();
  const { fingers, shape } = pathOf(found);
  const gaps = gapsOf(found);
  const arrivals = arrivalsOf(table, found.entry, notes, fingers);
  return {
    ok: true,
    fingers,
    need: needScores(tables.need, notes, fingers, arrivals, gaps),
    arrivals,
    gaps,
    cost: bestOf(found),
    join: found.entry.mode,
    state: leftState(keyboard, notes, fingers, shape, line),
  };
}

// ---------------------------------------------------------------------------
// The functions of the contract (section 3)
// ---------------------------------------------------------------------------

/** The fingering of one hand of one phrase, its need scores and the state it leaves. */
export function fingerHand(tables: Tables, tier: string, hand: Hand, input: HandInput): HandResult {
  const line = checkedLine(input.line);
  const key = checkedKey(input.key);
  const state = checkedState(input.state);
  const table = handTable(tables, tier, hand);
  if (table === null) return notOk();
  const keyboard = keyboardOf(table, key);
  const notes = notesOf(tables, table, keyboard, line);
  if (notes === null) return notOk();
  if (notes.length === 0) return silentResult(state);
  return table.kind === 'position'
    ? positionResult(tables, table, keyboard, notes, line, state)
    : motionResult(tables, table, keyboard, notes, line, state);
}

/** `fingerHand` per hand of a phrase; the hands do not interact. */
export function fingerPhrase(
  tables: Tables,
  tier: string,
  hands: Partial<Record<Hand, HandInput>>,
): Partial<Record<Hand, HandResult>> {
  return HANDS.reduce<Partial<Record<Hand, HandResult>>>((results, hand) => {
    const input = hands[hand];
    return input === undefined
      ? results
      : { ...results, [hand]: fingerHand(tables, tier, hand, input) };
  }, {});
}

/** Whether the line, sounding in `key`, can follow the state (contract 5.5). */
export function joinHand(
  tables: Tables,
  tier: string,
  hand: Hand,
  state: HandState | null,
  line: Line,
  key?: Key,
): Join {
  const tokens = checkedLine(line);
  const sounding = checkedKey(key);
  const start = checkedState(state);
  const table = handTable(tables, tier, hand);
  if (table === null) return NO_JOIN;
  const keyboard = keyboardOf(table, sounding);
  const notes = notesOf(tables, table, keyboard, tokens);
  if (notes === null) return NO_JOIN;
  const held = stateIndex(keyboard, start);
  if (table.kind === 'position' || notes.length === 0 || start === null || held === null) {
    return FREE_JOIN;
  }
  if (start.rested || notes[0].afterRest) return FREE_JOIN;
  const linked = search(tables, table, keyboard, { mode: 'linked', held, state: start }, notes);
  if (!isFound(linked)) return NO_JOIN;
  const placed = search(tables, table, keyboard, { mode: 'placed' }, notes);
  const neededAnyway = isFound(placed) ? Math.floor(bestOf(placed) / tables.detached_unit) : 0;
  return {
    compatible: true,
    hops: Math.max(0, Math.floor(bestOf(linked) / tables.detached_unit) - neededAnyway),
  };
}

/** Compatible when every hand is; the hops add up. */
export function joinPhrase(
  tables: Tables,
  tier: string,
  states: Partial<Record<Hand, HandState | null>>,
  lines: Partial<Record<Hand, Line>>,
  key?: Key,
): Join {
  return HANDS.reduce<Join>((total, hand) => {
    const line = lines[hand];
    if (line === undefined) return total;
    const join = joinHand(tables, tier, hand, states[hand] ?? null, line, key);
    return { compatible: total.compatible && join.compatible, hops: total.hops + join.hops };
  }, FREE_JOIN);
}

/** The join check of a line in one key from every possible state, as one
 *  character per (pitch, finger): 'x' not compatible, else the hops (capped at
 *  9). Empty: free from any state. A single 'x': the line holds a pitch the hand
 *  does not have, so it is compatible from no state, a missing one included
 *  (contract 6.2). */
export function entryRecord(
  tables: Tables,
  tier: string,
  hand: Hand,
  line: Line,
  key?: Key,
): string {
  const tokens = checkedLine(line);
  const sounding = checkedKey(key);
  const table = handTable(tables, tier, hand);
  if (table === null) throw new RangeError(`${tier} has no ${hand} table`);
  const keyboard = keyboardOf(table, sounding);
  if (notesOf(tables, table, keyboard, tokens) === null) return NEVER_COMPATIBLE;
  const alwaysFree =
    table.kind === 'position' || tokens.every((token) => token === null) || tokens[0] === null;
  if (alwaysFree) return '';
  const mark = (midi: number, finger: Finger): string => {
    const join = joinHand(tables, tier, hand, { midi, finger, rested: false }, tokens, sounding);
    return join.compatible ? `${Math.min(join.hops, 9)}` : NEVER_COMPATIBLE;
  };
  return keyboard.midi.map((midi) => FINGERS.map((finger) => mark(midi, finger)).join('')).join('');
}
